import { Modal } from '@chronosphereio/chrono-ui';
import { useEffect, useState } from 'react';
import { PanelGroupDefinition, PanelGroupEditorValues, usePanelGroupEditor } from '../../context';
import { PanelGroupEditorForm, PanelGroupEditorFormProps, panelGroupEditorFormId } from './PanelGroupEditorForm';

interface FormState {
  mode: 'Add' | 'Edit';
  initialValues: PanelGroupEditorValues;
  kind: PanelGroupDefinition['kind'];
}

const DEFAULT_FORM_STATE: FormState = {
  mode: 'Add',
  initialValues: { title: '', isCollapsed: false, splitEnabled: false },
  kind: 'Grid',
};

/**
 * A dialog for adding or editing a Panel Group. Open and initial state is controlled by the DashboardStore.
 */
export function PanelGroupDialog() {
  const panelGroupEditor = usePanelGroupEditor();
  const [isOpen, setIsOpen] = useState(false);
  const [formState, setFormState] = useState<FormState>(DEFAULT_FORM_STATE);

  // Cache editor values when modal opens
  useEffect(() => {
    if (panelGroupEditor !== undefined) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsOpen(true);
      setFormState({
        mode: panelGroupEditor.mode,
        initialValues: panelGroupEditor.initialValues,
        kind: panelGroupEditor.kind,
      });
    }
  }, [panelGroupEditor]);

  const handleClose = () => {
    setIsOpen(false);
    panelGroupEditor?.close();
  };

  const handleSubmit: PanelGroupEditorFormProps['onSubmit'] = (values) => {
    // This shouldn't happen since we don't render the submit button until we have a model, but check to make TS happy
    if (panelGroupEditor === undefined) {
      throw new Error('Cannot apply changes');
    }
    panelGroupEditor.applyChanges(values);
    handleClose();
  };

  const handlePrimaryClick = () => {
    // Trigger the form submission by getting the form element and calling submit
    const form = document.getElementById(panelGroupEditorFormId) as HTMLFormElement;
    if (form) {
      form.requestSubmit();
    }
  };

  return (
    <Modal
      open={isOpen}
      title={`${formState.mode} panel group`}
      onClose={handleClose}
      onPrimaryButtonClick={handlePrimaryClick}
      primaryButtonText={formState.mode === 'Edit' ? 'Apply' : 'Add'}
      size="small"
    >
      <PanelGroupEditorForm initialValues={formState.initialValues} onSubmit={handleSubmit} kind={formState.kind} />
    </Modal>
  );
}
